import { Spinner } from "@/components/common/Spinner";
import { AnimatedCheckmark } from "./AnimatedCheckmark";

export type RowState = "pending" | "checking" | "ok" | "missing" | "error";

interface ChecklistRowProps {
  icon: React.ReactNode;
  label: string;
  state: RowState;
  /** Subtitle shown under the label. Swapped in place when state changes. */
  detail?: string;
  /** For missing/error, an inline CTA to fix it. */
  action?: React.ReactNode;
}

/**
 * One row in the environment-check step.
 *
 * Lays out as: [icon][label + detail][status badge]. The status badge
 * is what animates between states — a pulsing dot during detection,
 * a drawn checkmark when the tool is ready, a warning glyph when it
 * isn't.
 */
export function ChecklistRow({
  icon,
  label,
  state,
  detail,
  action,
}: ChecklistRowProps) {
  return (
    <div
      className={`flex items-start gap-4 rounded-xl px-4 py-3.5 transition-colors duration-300
        ${
          state === "ok"
            ? "bg-oxy-50/70"
            : state === "missing" || state === "error"
              ? "bg-hydra-50/70"
              : "bg-surface"
        }`}
    >
      <div
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
          ${state === "ok" ? "bg-surface text-oxy" : state === "missing" || state === "error" ? "bg-surface text-hydra" : "bg-space-20 text-space-50"}`}
      >
        {icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 font-medium text-space">
          {label}
        </div>
        {detail && (
          <p
            className="mt-0.5 text-[14px] leading-snug text-space-50"
            // key forces the crossfade when text swaps
            key={detail}
            style={{ animation: "onbFadeIn 220ms ease-out both" }}
          >
            {detail}
          </p>
        )}
        {(state === "missing" || state === "error") && action && (
          <div className="mt-2">{action}</div>
        )}
      </div>

      <div className="flex h-8 w-8 shrink-0 items-center justify-center">
        {state === "pending" && (
          <span className="block h-2 w-2 rounded-full bg-space-30" />
        )}
        {state === "checking" && <Spinner size="sm" className="text-space-50" />}
        {state === "ok" && <AnimatedCheckmark size={22} tone="oxy" />}
        {(state === "missing" || state === "error") && (
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5 text-hydra"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9.5" opacity="0.35" />
            <path d="M12 8v5M12 16.2v.1" />
          </svg>
        )}
      </div>
    </div>
  );
}
